import { parseCreateOrderFailure, parseCancelOrderFailure } from '../utils/orderResultUtils';
import { suppressOrderRejectNotices } from '../utils/orderStatusNotice';

/** Normalize TradeStation order status for comparisons (handles non-string API values, spacing). */
export const normalizeOrderStatus = (status) => {
    if (status == null || status === '') return '';
    return String(status).trim().toUpperCase();
};

/** True when status represents a fully filled order (REST + stream). */
export const isOrderStatusFilled = (status) => {
    const n = normalizeOrderStatus(status);
    return n === 'FLL' || n === 'FILLED';
};

// Helper function to check if an order is truly active/cancelable
export const isOrderActive = (order) => {
    if (!order) return false;
    
    const status = normalizeOrderStatus(order.Status);
    const statusDescription = (order.StatusDescription || '').toLowerCase();
    
    // Check status first - if it's a working/open status, allow it even with ClosedDateTime
    // Note: "DON" (Done/Queued) can have ClosedDateTime but is still cancelable
    // Note: "OUT" is UROut (canceled / unfilled out) — not open; do not list as cancelable
    const openStatuses = ['ACK', 'DON', 'DONE', 'OPN', 'PLA', 'WORKING'];
    const isOpenStatus = openStatuses.includes(status);
    
    // PRIMARY CHECK: If order has ClosedDateTime AND it's not an open status, it's closed
    // Exception: DON status can have ClosedDateTime but is still cancelable
    if (order.ClosedDateTime && !isOpenStatus) {
        return false;
    }
    
    // SECONDARY CHECK: Explicitly exclude all terminal states
    // Include CANCELLED/CANCELED for Connect Trade payloads that skip TS "CAN" mapping.
    const terminalStatuses = ['CAN', 'CANCELLED', 'CANCELED', 'REPLACED', 'FLL', 'FILLED', 'REJ', 'EXP', 'FLP', 'OUT', 'OUT CAN', 'TSC'];
    if (terminalStatuses.includes(status)) {
        return false;
    }

    // A working status with the full quantity already filled is a stale row.
    // The orders table can show the fill while this copy still says OPN.
    const orderedQty = parseFloat(order.Quantity ?? order.Legs?.[0]?.QuantityOrdered);
    const filledQty = parseFloat(order.FilledQuantity ?? order.Legs?.[0]?.ExecQuantity);
    if (Number.isFinite(orderedQty) && orderedQty > 0 && Number.isFinite(filledQty) && filledQty >= orderedQty) {
        return false;
    }
    
    // TERTIARY CHECK: StatusDescription for canceled/filled indicators (but not for open statuses)
    if (!isOpenStatus && (statusDescription.includes('cancel') || statusDescription.includes('filled') || 
        statusDescription.includes('reject') || statusDescription.includes('expired'))) {
        return false;
    }
    
    // FINAL CHECK: Return true if it's an open status
    return isOpenStatus;
};

/**
 * Chart lines and order merges must not follow an order onto a different account.
 * Missing AccountID stays visible — some stream rows omit it and are already
 * scoped by the subscription. A present id has to match the selected account
 * or its Connect Trade UUID.
 */
export const orderMatchesSelectedAccount = (order, account) => {
    if (!account?.AccountID || !order?.AccountID) return true;
    const ref = String(order.AccountID);
    if (ref === String(account.AccountID)) return true;
    if (account.ct_account_id && ref === String(account.ct_account_id)) return true;
    return false;
};

export const getOpenOrders = (orders, selectedTicker, selectedAccount = null) => {
    if (!orders || orders.length === 0) {
        return [];
    }
    
    // Count TSLA orders at each filtering stage
    const tslaOrdersBeforeFilter = orders.filter(o => {
        const symbol = o.Symbol || o.Legs?.[0]?.Symbol;
        return symbol === selectedTicker;
    });
    
    if (selectedTicker === 'TSLA') {
        // Track filtering stages for TSLA orders
        const afterActiveCheck = tslaOrdersBeforeFilter.filter(o => isOrderActive(o));
        const afterSymbolCheck = afterActiveCheck; // Symbol already filtered above
        const afterAccountCheck = afterSymbolCheck.filter(o => {
            const accountMatch = selectedAccount 
                ? (!o.AccountID || String(o.AccountID) === String(selectedAccount.AccountID))
                : true;
            return accountMatch;
        });
        
        console.log(`[getOpenOrders] TSLA filtering stages:`, {
            beforeFilter: tslaOrdersBeforeFilter.length,
            afterActiveCheck: afterActiveCheck.length,
            afterAccountCheck: afterAccountCheck.length,
            final: afterAccountCheck.length,
            sampleOrders: tslaOrdersBeforeFilter.slice(0, 3).map(o => ({
                OrderID: o.OrderID,
                Status: o.Status,
                ClosedDateTime: o.ClosedDateTime,
                AccountID: o.AccountID,
                TradeAction: o.Legs?.[0]?.BuyOrSell || o.TradeAction,
                isActive: isOrderActive(o),
                accountMatch: selectedAccount 
                    ? (!o.AccountID || String(o.AccountID) === String(selectedAccount.AccountID))
                    : true
            }))
        });
    }
    
    const filtered = orders.filter((order) => {
        // Use centralized active check
        if (!isOrderActive(order)) {
            return false;
        }
        
        // Filter by ticker - use same fallback pattern as other functions
        const symbol = order.Symbol || order.Legs?.[0]?.Symbol;
        const symbolMatch = selectedTicker ? symbol === selectedTicker : true;
        if (!symbolMatch) {
            return false;
        }
        
        // Filter by account - be lenient if AccountID is missing, handle string/number mismatch
        const accountMatch = selectedAccount 
            ? (!order.AccountID || String(order.AccountID) === String(selectedAccount.AccountID))
            : true;
        
        return accountMatch;
    });
    
    return filtered;
}

export const getOpenStopLossOrderFromPlacedOrders = (placedOrders) => {
    const filterFunc = (order) => {
        return order.Message.includes('Stop Market')
    }
    return placedOrders.filter(filterFunc);
}

export const getOpenStopLossOrders = (openOrders, selectedTicker = null) => {
    const filterFunc = (order) => {
        const symbol = order.Symbol || order.Legs?.[0]?.Symbol;
        const isStopMarket = order.OrderType === 'StopMarket';
        const isActiveStatus = order.Status === 'ACK' || order.Status === 'OPN' || order.Status === 'WORKING' || order.Status === 'DON';
        const isCorrectTicker = selectedTicker ? symbol === selectedTicker : true;
        
        return isStopMarket && isActiveStatus && isCorrectTicker;
    }
    return openOrders.filter(filterFunc);
}

export const getOpenTakeProfitOrders = (openOrders, selectedTicker = null) => {
    const filterFunc = (order) => {
        const symbol = order.Symbol || order.Legs?.[0]?.Symbol;
        const isLimitOrder = order.OrderType === 'Limit';
        const isActiveStatus = order.Status === 'ACK' || order.Status === 'OPN' || order.Status === 'WORKING' || order.Status === 'DON';
        const isCorrectTicker = selectedTicker ? symbol === selectedTicker : true;
        
        return isLimitOrder && isActiveStatus && isCorrectTicker;
    }
    return openOrders.filter(filterFunc);
}

// Helper function to identify bracket orders and get only parent orders to cancel
const getBracketParentOrders = (orders) => {
    const bracketGroups = new Map();
    const orderMap = new Map();
    
    // First pass: Create a map of all orders by OrderID
    orders.forEach(order => {
        orderMap.set(order.OrderID, order);
    });
    
    // Second pass: Group orders by bracket relationships
    orders.forEach(order => {
        const advancedOptions = order.AdvancedOptions || '';
        const groupName = order.GroupName || '';
        
        // Check if this is a bracket child order (has BRK in AdvancedOptions or GroupName starts with "Brk")
        const isBracketChild = advancedOptions.includes('BRK=') || groupName.startsWith('Brk');
        
        if (isBracketChild) {
            // Extract bracket group ID from GroupName (e.g., "Brk 6323551222" -> "6323551222")
            const bracketIdMatch = groupName.match(/Brk\s+(\d+)/);
            const bracketId = bracketIdMatch ? bracketIdMatch[1] : null;
            
            if (bracketId) {
                if (!bracketGroups.has(bracketId)) {
                    bracketGroups.set(bracketId, { children: [], parent: null });
                }
                bracketGroups.get(bracketId).children.push(order);
                
                // Find the parent order that this child references
                const conditionalOrders = order.ConditionalOrders || [];
                const parentRef = conditionalOrders.find(co => co.Relationship === 'OSP');
                if (parentRef && orderMap.has(parentRef.OrderID)) {
                    const potentialParent = orderMap.get(parentRef.OrderID);
                    // Only set as parent if it's not already marked (should be same for all children)
                    if (!bracketGroups.get(bracketId).parent) {
                        bracketGroups.get(bracketId).parent = potentialParent;
                    }
                }
            }
        }
    });
    
    const ordersToCancel = [];
    const processedOrderIds = new Set();
    const parentOrdersToCancel = new Set(); // Track unique parent orders
    
    // For each bracket group, add only the parent order to cancel (deduplicated)
    bracketGroups.forEach((group, bracketId) => {
        const { parent, children } = group;
        
        // Filter out already-canceled or filled orders using centralized check
        const activeChildren = children.filter(order => isOrderActive(order));
        
        if (parent) {
            const isParentActive = isOrderActive(parent);
            
            if (isParentActive && activeChildren.length > 0) {
                // Check if we've already added this parent (multiple bracket groups can share same parent)
                if (!parentOrdersToCancel.has(parent.OrderID)) {
                    console.log(`Bracket group ${bracketId}: Will cancel parent order ${parent.OrderID} (${activeChildren.length} active children will auto-cancel)`);
                    ordersToCancel.push(parent);
                    parentOrdersToCancel.add(parent.OrderID);
                    processedOrderIds.add(parent.OrderID);
                } else {
                    console.log(`Bracket group ${bracketId}: Parent order ${parent.OrderID} already queued for cancellation (shared parent)`);
                }
                // Mark all children as processed so they won't be added as standalone orders
                children.forEach(child => processedOrderIds.add(child.OrderID));
            } else if (!isParentActive && activeChildren.length > 0) {
                console.log(`Bracket group ${bracketId}: Parent already canceled, but ${activeChildren.length} children still active (unusual state)`);
                // In this unusual case, cancel the active children
                activeChildren.forEach(child => {
                    if (!processedOrderIds.has(child.OrderID)) {
                        ordersToCancel.push(child);
                        processedOrderIds.add(child.OrderID);
                    }
                });
            } else {
                console.log(`Bracket group ${bracketId}: All orders already canceled/filled`);
                if (parent) processedOrderIds.add(parent.OrderID);
                children.forEach(child => processedOrderIds.add(child.OrderID));
            }
        } else {
            // Couldn't identify parent, cancel all active children
            if (activeChildren.length > 0) {
                activeChildren.forEach(child => {
                    if (!processedOrderIds.has(child.OrderID)) {
                        ordersToCancel.push(child);
                        processedOrderIds.add(child.OrderID);
                    }
                });
            }
            children.forEach(child => processedOrderIds.add(child.OrderID));
        }
    });
    
    // Add any standalone orders (not part of bracket groups) - using centralized active check
    orders.forEach(order => {
        if (!processedOrderIds.has(order.OrderID) && isOrderActive(order)) {
            ordersToCancel.push(order);
        }
    });
    
    return ordersToCancel;
};

export const cancelOrders = async (isPaperTrading, orders, cancelOrderFn, showMessage) => {
    if (orders && orders.length) {
        // Optimize for bracket orders - only cancel parent orders
        const ordersToCancel = getBracketParentOrders(orders);
        
        let successCount = 0;
        let errorCount = 0;
        let errorMessages = [];
        let canceledOrderIds = [];
        
        // Cancel all orders in parallel for faster execution
        const cancelPromises = ordersToCancel.map(async (order) => {
            const orderType = order.OrderType || 'Unknown';
            const side = order.Legs?.[0]?.BuyOrSell || '';
            const orderDesc = `${order.OrderID} (${side} ${orderType})`;
            
            try {
                const result = await cancelOrderFn(isPaperTrading, order.OrderID, order.AccountID);
                const failure = parseCancelOrderFailure(result);
                if (failure) {
                    if (failure.includes('Not an open order') || failure.includes('Invalid order ID')) {
                        console.log(`ℹ️ Order ${orderDesc} was already canceled/filled`);
                        return { success: false, orderId: order.OrderID, error: null, alreadyCanceled: true };
                    }
                    console.warn(`❌ Failed to cancel order ${orderDesc}:`, failure);
                    return { success: false, orderId: order.OrderID, error: failure, alreadyCanceled: false };
                }
                return { success: true, orderId: order.OrderID, error: null, alreadyCanceled: false };
            } catch (error) {
                const errorMsg = error.message || 'Unknown error';
                // Don't count "Not an open order" or "Invalid order ID" as errors if we had some success
                if (errorMsg.includes('Not an open order') || errorMsg.includes('Invalid order ID')) {
                    console.log(`ℹ️ Order ${orderDesc} was already canceled/filled`);
                    return { success: false, orderId: order.OrderID, error: null, alreadyCanceled: true };
                } else {
                    console.warn(`❌ Error cancelling order ${orderDesc}:`, errorMsg);
                    return { success: false, orderId: order.OrderID, error: errorMsg, alreadyCanceled: false };
                }
            }
        });
        
        // Wait for all cancellation requests to complete in parallel
        const results = await Promise.allSettled(cancelPromises);
        
        // Process results
        results.forEach((result) => {
            if (result.status === 'fulfilled') {
                const { success, orderId, error, alreadyCanceled } = result.value;
                if (success) {
                    successCount++;
                    canceledOrderIds.push(orderId);
                } else if (!alreadyCanceled && error) {
                    errorCount++;
                    errorMessages.push(error);
                }
            } else {
                // Handle promise rejection (shouldn't happen with our try-catch, but just in case)
                errorCount++;
                errorMessages.push(result.reason?.message || 'Unknown error');
            }
        });
        
        // Show appropriate message based on results
        if (showMessage) {
            if (successCount > 0 && errorCount === 0) {
                // Show order IDs for successful cancellations
                const orderIdText = canceledOrderIds.length <= 3 
                    ? canceledOrderIds.join(', ')
                    : `${canceledOrderIds.slice(0, 2).join(', ')} +${canceledOrderIds.length - 2} more`;
                showMessage(`Cancelled order${successCount > 1 ? 's' : ''} ${orderIdText}`, 'success');
            } else if (successCount > 0 && errorCount > 0) {
                const uniqueErrors = [...new Set(errorMessages)];
                const errorSummary = uniqueErrors.length > 1 
                    ? `${errorCount} orders failed (${uniqueErrors.slice(0, 2).join(', ')}${uniqueErrors.length > 2 ? '...' : ''})`
                    : `${errorCount} orders failed (${uniqueErrors[0]})`;
                const orderIdText = canceledOrderIds.length <= 2 
                    ? canceledOrderIds.join(', ')
                    : `${canceledOrderIds.slice(0, 2).join(', ')} +${canceledOrderIds.length - 2}`;
                showMessage(`Cancelled ${orderIdText}, ${errorSummary}`, 'warning');
            } else if (successCount === 0 && errorCount > 0) {
                const uniqueErrors = [...new Set(errorMessages)];
                const errorSummary = uniqueErrors.length > 1 
                    ? `${uniqueErrors.slice(0, 2).join(', ')}${uniqueErrors.length > 2 ? '...' : ''}`
                    : uniqueErrors[0];
                showMessage(`Failed to cancel orders: ${errorSummary}`, 'warning');
            }
        }
        return { successCount, errorCount, canceledOrderIds, errorMessages };
    }
    return { successCount: 0, errorCount: 0, canceledOrderIds: [], errorMessages: [] };
}

export const validatePlacedOrders = (orders) => {
    let hasFailed = false;
    let messages = [];
    orders.forEach(order => {
        if (order.Error === "FAILED" || order.Status === 'REJ' || order.Status === 'FAILED') {
            hasFailed = true;
            messages.push(order.Message || order.RejectReason || order.Reason || 'Order failed');
        }
    });
    return { hasFailed, messages };
}

export const submitOrders = async (isPaperTrading, orders, createOrderFn, showMessage, setStopLossOrders) => {
    let allOrders = [];

    for (const order of orders) {
        showMessage('Submitting order...');
        const result = await createOrderFn(isPaperTrading, order);
        const failure = parseCreateOrderFailure(result);
        if (failure) {
            suppressOrderRejectNotices(result);
            showMessage(failure, 'danger');
            return;
        }
        if (result?.Orders) {
            allOrders = allOrders.concat(result.Orders);
        }
    }

    const validation = validatePlacedOrders(allOrders);

    if (validation.hasFailed) {
        showMessage(validation.messages.join('. '), 'danger');
    } else {
        showMessage('Order successfully placed', 'success');
        const stopLossOrders = getOpenStopLossOrderFromPlacedOrders(allOrders);
        if (stopLossOrders.length > 0) {
            setStopLossOrders(stopLossOrders);
        }
    }
}

export const transformBarValues = (bars) => {
    return bars.map(bar => {
        let transformedBar = {};

        for (let key in bar) {
            let value = bar[key];

            // Check if the value can be converted to a number
            if (!isNaN(parseFloat(value)) && isFinite(value)) {
                transformedBar[key] = Number(value);
            } else {
                transformedBar[key] = value;
            }
        }

        return transformedBar;
    });
}

/** @param {Record<string, unknown>} leg */
const parseLegExecutionTimeMs = (leg) => {
    if (!leg) return null;
    const raw =
        leg.ExecutionTimeStamp ||
        leg.ExecutionTime ||
        leg.ExecutedTime ||
        leg.FillTime ||
        leg.ExecutionDateTime ||
        null;
    if (!raw) return null;
    const t = new Date(raw).getTime();
    return Number.isFinite(t) && t > 0 ? t : null;
};

/** Best-effort fill instant (prefers per-leg execution time, then ClosedDateTime). */
export const getOrderFillTimeMs = (order) => {
    const leg = order.Legs?.[0];
    const fromLeg = parseLegExecutionTimeMs(leg);
    if (fromLeg != null) return fromLeg;
    const candidates = [order.ClosedDateTime, order.FilledDateTime, order.OpenedDateTime];
    for (const c of candidates) {
        if (!c) continue;
        const t = new Date(c).getTime();
        if (Number.isFinite(t) && t > 0) return t;
    }
    return null;
};

/**
 * Convert filled orders to chart markers for trade execution display
 * @param {Array} orders - Array of order objects
 * @param {string} ticker - The ticker symbol to filter orders for
 * @param {{ buyColor?: string, sellColor?: string }} colors - Buy = long/add, sell = short/reduce
 * @returns {Array} Array of marker objects for lightweight-charts
 */
export const createTradeExecutionMarkers = (orders, ticker, { buyColor = '#07CBA3', sellColor = '#F73263' } = {}) => {
    if (!Array.isArray(orders) || !ticker) return [];

    const filledOrders = orders.filter((order) => {
        if (!order) return false;
        const isFilled = order.Status === 'FLL' || order.Status === 'FLP';
        if (!isFilled) return false;
        const orderSymbol = order.Legs?.[0]?.Symbol || order.Symbol;
        if (orderSymbol !== ticker) return false;
        if (getOrderFillTimeMs(order) == null) return false;
        return true;
    });

    // De-dupe by order id (stream + historical can briefly duplicate); keep latest fill time
    const byId = new Map();
    for (const order of filledOrders) {
        const oid = order.OrderID ?? order.orderId;
        const key = oid != null ? String(oid) : `${order.OpenedDateTime}-${order.Legs?.[0]?.ExecQuantity}`;
        const t = getOrderFillTimeMs(order);
        const prev = byId.get(key);
        if (!prev || t > prev._t) {
            byId.set(key, { order, _t: t });
        }
    }
    const uniqueOrders = [...byId.values()].map((x) => x.order);

    const markers = uniqueOrders
        .map((order) => {
            const fillPrice = parseFloat(order.Legs?.[0]?.ExecutionPrice || order.FilledPrice || 0);
            if (!fillPrice || fillPrice === 0) {
                return null;
            }

            const tMs = getOrderFillTimeMs(order);
            if (tMs == null) return null;
            const timeInSeconds = Math.floor(tMs / 1000);

            const quantity = Math.abs(parseFloat(order.Legs?.[0]?.ExecQuantity || 0));
            if (!quantity || quantity === 0) {
                return null;
            }

            const buyOrSell = order.Legs?.[0]?.BuyOrSell || 'Buy';
            const isBuy = String(buyOrSell).toUpperCase() === 'BUY';
            const formattedPrice = fillPrice.toFixed(2);
            const formattedQuantity = Math.floor(quantity);
            const oid = order.OrderID ?? order.orderId;

            return {
                time: timeInSeconds,
                price: fillPrice,
                color: isBuy ? buyColor : sellColor,
                side: isBuy ? 'buy' : 'sell',
                text: `${isBuy ? '+' : '-'}${formattedQuantity}`,
                id: oid != null ? String(oid) : undefined,
                hintPriceText: `$${formattedPrice}`,
            };
        })
        .filter(Boolean);

    markers.sort((a, b) => a.time - b.time);
    return markers;
};

/**
 * Chart markers from local order_events rows (one marker per fill).
 * @param {Array} events - { occurredAt, price, side, qty, brokerOrderId, eventUid, id }
 * @param {{ buyColor?: string, sellColor?: string }} colors
 */
export const createTradeExecutionMarkersFromEvents = (
    events,
    { buyColor = '#07CBA3', sellColor = '#F73263' } = {}
) => {
    if (!Array.isArray(events) || events.length === 0) return [];

    const markers = events
        .map((event) => {
            if (!event) return null;
            const fillPrice = parseFloat(event.price);
            if (!fillPrice || fillPrice === 0) return null;

            const tMs = event.occurredAt ? new Date(event.occurredAt).getTime() : NaN;
            if (!Number.isFinite(tMs)) return null;
            const timeInSeconds = Math.floor(tMs / 1000);

            const quantity = Math.abs(parseFloat(event.qty));
            if (!quantity || quantity === 0) return null;

            const isBuy = String(event.side || '').toLowerCase() === 'buy';
            const formattedPrice = fillPrice.toFixed(2);
            const formattedQuantity = Math.floor(quantity);
            const id =
                event.eventUid != null
                    ? String(event.eventUid)
                    : event.brokerOrderId != null
                      ? String(event.brokerOrderId)
                      : event.id != null
                        ? String(event.id)
                        : undefined;

            return {
                time: timeInSeconds,
                price: fillPrice,
                color: isBuy ? buyColor : sellColor,
                side: isBuy ? 'buy' : 'sell',
                text: `${isBuy ? '+' : '-'}${formattedQuantity}`,
                id,
                hintPriceText: `$${formattedPrice}`,
                brokerOrderId: event.brokerOrderId != null ? String(event.brokerOrderId) : undefined,
            };
        })
        .filter(Boolean);

    markers.sort((a, b) => a.time - b.time);
    return markers;
};

/**
 * Prefer DB fill markers; keep live stream markers for fills not yet in the ledger.
 */
export const mergeTradeExecutionMarkers = (dbMarkers, liveMarkers) => {
    const fromDb = Array.isArray(dbMarkers) ? dbMarkers : [];
    const fromLive = Array.isArray(liveMarkers) ? liveMarkers : [];
    if (fromDb.length === 0) return fromLive;
    if (fromLive.length === 0) return fromDb;

    const coveredOrderIds = new Set();
    for (const m of fromDb) {
        if (m?.brokerOrderId) coveredOrderIds.add(String(m.brokerOrderId));
        if (m?.id) coveredOrderIds.add(String(m.id));
    }

    const extras = fromLive.filter((m) => {
        if (!m?.id) return true;
        return !coveredOrderIds.has(String(m.id));
    });

    const merged = [...fromDb, ...extras];
    merged.sort((a, b) => a.time - b.time);
    return merged;
};

/** Count positions with non-zero quantity (open positions). */
export const countOpenPositions = (positions) => {
    if (!Array.isArray(positions)) return 0;
    return positions.filter((p) => Math.abs(parseFloat(p?.Quantity) || 0) > 0).length;
};

/** Count unique orders filled on the current local calendar day. */
export const countOrdersFilledToday = (orders) => {
    if (!Array.isArray(orders) || orders.length === 0) return 0;

    const todayStr = new Date().toDateString();
    const seen = new Set();
    let count = 0;

    for (const order of orders) {
        if (!order || !isOrderStatusFilled(order.Status)) continue;

        const fillMs = getOrderFillTimeMs(order);
        if (fillMs == null) continue;
        if (new Date(fillMs).toDateString() !== todayStr) continue;

        const oid = order.OrderID ?? order.orderId;
        const key =
            oid != null
                ? String(oid)
                : `${order.ClosedDateTime}-${order.Legs?.[0]?.ExecQuantity}`;
        if (seen.has(key)) continue;
        seen.add(key);
        count += 1;
    }

    return count;
};